import React from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import PrivateRoute from '../modules/profile/components/PrivateRoute';
import { Role } from '../models';
import RouteKey from './key';
import SelectSubjects from '../pages/specialConsideration/ApplyForSpecialConsideration/SelectSubjects';
import UploadSupportingDocuments from '../pages/specialConsideration/ApplyForSpecialConsideration/UploadSupportingDocuments';
import ReviewApplication from '../pages/specialConsideration/ApplyForSpecialConsideration/ReviewApplication';
import ApplicationSummary from '../pages/specialConsideration/ApplicationSummary';
import ApplicationDetails from '../pages/specialConsideration/ApplicationDetails';
import CoordinatorApplicationReview from '../pages/groupSpecialConsideration/ApplicationReview';
import CoordinatorApplicationSubmitted from '../pages/groupSpecialConsideration/ApplicationSubmitted';
import CoordinatorApplicationSummary from '../pages/groupSpecialConsideration/ApplicationSummary';
import UploadRankingDetail from '../pages/groupSpecialConsideration/UploadRankingDetail';
import SpecialConsiderationInstructionsPage from '../pages/specialConsideration/Instructions';
import SpecialConsiderationListingPage from '../pages/specialConsideration/Listing';
import UploadCoordinatorSupportingDocuments from '../pages/groupSpecialConsideration/UploadSupportingDocuments';

const { error: errorRoute } = RouteKey.child;
const {
  specialConsideration,
  instructions,
  selectSubject,
  amendSelectSubject,
  uploadSupportingDocument,
  amendUploadSupportingDocument,
  reviewApplication,
  amendReviewApplication,
  applicationSummary,
  applicationDetails,
  coordinatorUploadRankingDetails,
  amendCoordinatorUploadRankingDetails,
  coordinatorUploadSupportingDocument,
  amendCoordinatorUploadSupportingDocument,
  coordinatorApplicationReview,
  amendCoordinatorApplicationReview,
  coordinatorApplicationSubmitted,
  coordinatorApplicationSummary,
} = RouteKey['special-consideration'].child;

const SpecialConsiderationRoute = () => {
  const { path } = useRouteMatch();
  const { t } = useTranslation();

  const specialConsiderationTitle = t(
    'specialConsideration->>Instructions.ApplyForSpecialConsideration'
  );

  const routeSteps = [
    { label: t('specialConsideration->>ApplyForSpecialConsideration.SelectSubjectPaper') },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Upload Supporting Document(s)'),
    },
    { label: t('specialConsideration->>ApplyForSpecialConsideration.Review Application') },
  ];

  const amendmentRouteSteps = [
    { label: t('specialConsideration->>ApplyForSpecialConsideration.ViewSelectedSubject') },
    {
      label: t('specialConsideration->>ApplyForSpecialConsideration.Upload Supporting Document(s)'),
    },
    { label: t('specialConsideration->>ApplyForSpecialConsideration.Review Application') },
  ];

  const coordinatorRouteSteps = [
    { label: t('specialConsideration->>ApplyForSpecialConsideration.UploadRankingDetails') },
    { label: t('specialConsideration->>ApplyForSpecialConsideration.UploadSupportingDocuments') },
    { label: t('specialConsideration->>ApplyForSpecialConsideration.ReviewApplication') },
  ];

  return (
    <Switch>
      <PrivateRoute exact path={`${path}`} roles={[Role.candidate, Role.coordinator]}>
        <Redirect to={`${path}/${specialConsideration.route}`} />
      </PrivateRoute>
      <PrivateRoute
        exact
        path={`${path}/${specialConsideration.route}`}
        roles={[Role.candidate, Role.coordinator]}
      >
        <SpecialConsiderationListingPage />
      </PrivateRoute>
      <PrivateRoute
        exact
        path={`${path}/${specialConsideration.route}/${instructions.route}`}
        roles={[Role.candidate, Role.coordinator]}
      >
        <SpecialConsiderationInstructionsPage />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${selectSubject.route}`}
        roles={[Role.candidate]}
      >
        <SelectSubjects
          routeSteps={routeSteps}
          title={specialConsiderationTitle}
          amendment={false}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${amendSelectSubject.route}`}
        roles={[Role.candidate]}
      >
        <SelectSubjects
          routeSteps={amendmentRouteSteps}
          title={specialConsiderationTitle}
          amendment
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${uploadSupportingDocument.route}`}
        roles={[Role.candidate]}
      >
        <UploadSupportingDocuments routeSteps={routeSteps} amendment={false} />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${amendUploadSupportingDocument.route}`}
        roles={[Role.candidate]}
      >
        <UploadSupportingDocuments routeSteps={amendmentRouteSteps} amendment />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${reviewApplication.route}`}
        roles={[Role.candidate]}
      >
        <ReviewApplication
          routeSteps={routeSteps}
          title={specialConsiderationTitle}
          amendment={false}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${amendReviewApplication.route}`}
        roles={[Role.candidate]}
      >
        <ReviewApplication
          routeSteps={amendmentRouteSteps}
          title={specialConsiderationTitle}
          amendment
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${applicationSummary.route}`}
        roles={[Role.candidate]}
      >
        <ApplicationSummary />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${applicationDetails.route}`}
        roles={[Role.candidate]}
      >
        <ApplicationDetails />
      </PrivateRoute>

      {/* Coordinator Route */}
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${coordinatorUploadRankingDetails.route}`}
        roles={[Role.coordinator]}
      >
        <UploadRankingDetail routeSteps={coordinatorRouteSteps} amendment={false} />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${amendCoordinatorUploadRankingDetails.route}`}
        roles={[Role.coordinator]}
      >
        <UploadRankingDetail routeSteps={coordinatorRouteSteps} amendment />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${coordinatorUploadSupportingDocument.route}`}
        roles={[Role.coordinator]}
      >
        <UploadCoordinatorSupportingDocuments
          routeSteps={coordinatorRouteSteps}
          amendment={false}
        />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${amendCoordinatorUploadSupportingDocument.route}`}
        roles={[Role.coordinator]}
      >
        <UploadCoordinatorSupportingDocuments routeSteps={coordinatorRouteSteps} amendment />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${coordinatorApplicationReview.route}`}
        roles={[Role.coordinator]}
      >
        <CoordinatorApplicationReview routeSteps={coordinatorRouteSteps} amendment={false} />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${amendCoordinatorApplicationReview.route}`}
        roles={[Role.coordinator]}
      >
        <CoordinatorApplicationReview routeSteps={coordinatorRouteSteps} amendment />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${coordinatorApplicationSubmitted.route}`}
        roles={[Role.coordinator]}
      >
        <CoordinatorApplicationSubmitted />
      </PrivateRoute>
      <PrivateRoute
        path={`${path}/${specialConsideration.route}/:uuid/${coordinatorApplicationSummary.route}`}
        roles={[Role.coordinator]}
      >
        <CoordinatorApplicationSummary />
      </PrivateRoute>

      <Route path="*" exact>
        <Redirect to={`/${errorRoute}`} />
      </Route>
    </Switch>
  );
};

export default SpecialConsiderationRoute;
